<script setup lang="ts">
const {t} = useI18n()
const route = useRoute()
useHead({
  title: t('main.pageTitle'),
  link: [
    {
      rel: 'icon',
      type: 'image/png',
      href: '/assets/img/logo.png'
    },
    {
      rel: 'canonical',
      href: `https://www.InTheirLovingMemory.com${route.path}`
    }
  ],
  meta: []
})

import {AvailableLanguages, useLanguageStore} from "~/stores/useLanguageStore";
import type {LanguageOption} from '~/types/language';

const localeStore = useLanguageStore()
const languages = AvailableLanguages;
const auth = useAuthStore();
const cart = useUserCartStore();

const isNavActive = ref(false);
const isLangActive = ref(false);
const isStoriesMenuActive = ref(false);
const handleNavClick = (event: Event) => {
  const target = event.target as HTMLElement;

  // 1. Check if the click was on a toggle button or inside a dropdown
  const isDropdownToggle = target.closest('.toggle-dropdown');
  const isInsideSubmenu = target.closest('.submenu');

  // 2. Only close if it's NOT a dropdown action
  if (!isDropdownToggle && !isInsideSubmenu) {
    isNavActive.value = false;
  }
};
useHead({
  bodyAttrs: {
    class: computed(() => isNavActive.value ? 'mobile-nav-active' : '')
  }
})

const changeLanguage = (lang: LanguageOption) => {
  localeStore.setLanguage(lang);
}

onMounted(() => {
  cart.loadFromDisk();
})
</script>

<template>
  <header id="header" class="header d-flex align-items-center fixed-top">
    <div class="container-fluid position-relative d-flex align-items-center">
      <a href="/" class="logo d-flex align-items-center me-auto">
        <h1 class="sitename">{{ $t('main.siteName') }}<span>.</span></h1>
      </a>
      <nav id="navmenu" class="navmenu" @click="handleNavClick">
        <ul>
          <li>
            <NuxtLinkLocale :to="{ path: '/', hash: '#hero',  }">{{ $t('menu.home') }}</NuxtLinkLocale>
          </li>
          <li>
            <NuxtLinkLocale :to="{ path: '/', hash: '#how-it-works' }">{{ $t('menu.howItWorks') }}</NuxtLinkLocale>
          </li>
          <li>
            <NuxtLinkLocale :to="{ path: '/', hash: '#faq' }">{{ $t('menu.faq') }}</NuxtLinkLocale>
          </li>
          <li>
            <NuxtLinkLocale :to="{ path: '/', hash: '#about' }">{{ $t('menu.about') }}</NuxtLinkLocale>
          </li>
          <li>
            <NuxtLinkLocale :to="{ path: '/', hash: '#contact' }">{{ $t('menu.contact') }}</NuxtLinkLocale>
          </li>
          <li class="dropdown" v-if="auth.isLoggedIn" @click="isStoriesMenuActive = !isStoriesMenuActive"
              :class="{'active': isStoriesMenuActive}">
          <span class="justify-content-start">
            <span class="p-0 toggle-dropdown">{{ $t('menu.stories.head') }}</span>
            <i class="bi bi-chevron-down toggle-dropdown"></i>
          </span>
            <ul :class="{'dropdown-active': isStoriesMenuActive}" v-if="isStoriesMenuActive">
              <li>
                <NuxtLinkLocale to="/stories">{{ $t('menu.stories.myStories') }}</NuxtLinkLocale>
              </li>
              <li>
                <NuxtLinkLocale to="/qrcodes">{{ $t('menu.stories.qrCodes') }}</NuxtLinkLocale>
              </li>
              <li>
                <NuxtLinkLocale to="/validity">{{ $t('menu.stories.validity') }}</NuxtLinkLocale>
              </li>
              <li>
                <NuxtLinkLocale to="/gift-cards">{{ $t('menu.stories.giftCards') }}</NuxtLinkLocale>
              </li>
            </ul>
          </li>
          <li id="menu-cart" v-if="auth.isLoggedIn">
            <NuxtLinkLocale to="/cart">
              <div class="container d-flex align-items-center p-0 m-0">
                <span class="p-0">{{ $t('menu.cart') }}</span>
                <i class="bi bi-cart3"></i>
                <small v-if="cart.items.length > 0" class="badge rounded-pill bg-danger mx-1">{{ cart.items.length }}</small>
              </div>
            </NuxtLinkLocale>
          </li>
          <li class="dropdown language"
              :class="{'active': isLangActive}"
              @click="isLangActive = !isLangActive"
          >
          <span class="justify-content-start">
            <span class="p-0 toggle-dropdown">{{ localeStore.selected.flag }}</span>
            <i class="bi bi-chevron-down toggle-dropdown"></i>
          </span>
            <ul :class="{'dropdown-active': isLangActive}" v-if="isLangActive">
              <li v-for="lang in languages" :key="lang.label">
                <span @click="changeLanguage(lang)">{{ lang.flag }} {{ lang.label }} ({{ lang.currency }})</span>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
      <i
          class="mobile-nav-toggle d-xl-none bi"
          @click="isNavActive = !isNavActive"
          :class="{'bi-list' : !isNavActive, 'bi-x' : isNavActive}"
      ></i>
      <NuxtLinkLocale to="stories" class="btn-getstarted" v-if="!auth.isLoggedIn">{{ $t('menu.getStarted') }}</NuxtLinkLocale>
    </div>
  </header>
</template>

<style scoped>
.header {
  --background-color: rgba(34, 59, 81, 0.9);
  --default-color: #ffffff;
  --heading-color: #ffffff;
  color: var(--default-color);
  background-color: var(--background-color);
  transition: all 0.5s;
  z-index: 997;
}

.header .container-fluid {
  max-width: 1600px;
}

.header .logo {
  line-height: 1;
}

.header .logo img {
  max-height: 36px;
  margin-right: 8px;
}

.header .logo h1 {
  font-size: clamp(1rem, 5vw, 2rem);
  margin: 0;
  font-weight: 700;
  color: var(--heading-color);
}

.header .logo span {
  color: var(--accent-color);
  font-size: 30px;
  font-weight: 700;
}

.header .btn-getstarted,
.header .btn-getstarted:focus {
  color: var(--contrast-color);
  font-size: 14px;
  padding: 6px 30px;
  margin: 12px 0 12px 30px;
  border-radius: 50px;
  transition: 0.3s;
  border: 2px solid var(--accent-color);
}

.header .btn-getstarted:hover,
.header .btn-getstarted:focus:hover {
  color: var(--contrast-color);
  background: var(--accent-color);
}

.dropdown {
  cursor: pointer;
}

/*--------------------------------------------------------------
# Navigation Menu
--------------------------------------------------------------*/
/* Navmenu - Desktop */
@media (min-width: 1200px) {
  .navmenu {
    padding: 0;
  }

  .navmenu ul {
    margin: 0;
    padding: 0;
    display: flex;
    list-style: none;
    align-items: center;
  }

  .navmenu li {
    position: relative;
  }

  .navmenu a,
  .navmenu span,
  .navmenu a:focus {
    color: var(--nav-color);
    padding: 18px 15px;
    font-size: 16px;
    font-family: var(--nav-font), serif;
    font-weight: 400;
    display: flex;
    align-items: center;
    justify-content: space-between;
    white-space: nowrap;
    transition: 0.3s;
  }

  .navmenu a i,
  .navmenu a:focus i {
    font-size: 12px;
    line-height: 0;
    margin-left: 5px;
    transition: 0.3s;
  }

  .navmenu li:last-child a {
    padding-right: 0;
  }

  .navmenu li:hover > a,
  .navmenu li:hover > span,
  .navmenu li:hover > span > span,
  .navmenu,
  .navmenu .router-link-active:focus {
    color: var(--nav-hover-color);
  }

  .navmenu .dropdown ul {
    margin: 0;
    padding: 10px 0;
    background: var(--nav-dropdown-background-color);
    display: block;
    position: absolute;
    visibility: hidden;
    left: 14px;
    top: 130%;
    opacity: 0;
    transition: 0.3s;
    border-radius: 4px;
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.1);
  }

  .navmenu .language ul {
    left: -150px;
  }

  .navmenu .dropdown ul li {
    min-width: 200px;
  }

  .navmenu .dropdown ul a,
  .navmenu .dropdown ul span {
    padding: 10px 20px;
    font-size: 15px;
    text-transform: none;
    color: var(--nav-dropdown-color);
  }

  .navmenu .dropdown ul a i {
    font-size: 12px;
  }

  .navmenu .dropdown ul a:hover,
  .navmenu .dropdown ul span:hover,
  .navmenu .dropdown ul .active:hover,
  .navmenu .dropdown ul li:hover > a {
    color: var(--nav-dropdown-hover-color);
    cursor: pointer;
  }

  .navmenu .dropdown:hover > ul {
    opacity: 1;
    top: 100%;
    visibility: visible;
  }

  .navmenu .dropdown .dropdown ul {
    top: 0;
    left: -90%;
    visibility: hidden;
  }

  .navmenu .dropdown .dropdown:hover > ul {
    opacity: 1;
    top: 0;
    left: -100%;
    visibility: visible;
  }
}

@media (max-width: 1200px) {
  .header .logo {
    order: 1;
  }


  .header .btn-getstarted,
  .header .btn-getstarted:hover {
    order: 2;
    margin: 0 10px 0 0;
    padding: 6px 10px;
    text-align: center;
  }

  .header .mobile-nav-toggle {
    order: 3;
    width: fit-content;
  }

  .header {
    height: 60px;
  }
}

/* Navmenu - Mobile */
@media (max-width: 1199px) {
  .mobile-nav-toggle {
    color: var(--nav-color);
    font-size: 28px;
    line-height: 0;
    cursor: pointer;
    transition: color 0.3s;
  }

  .navmenu {
    padding: 0;
    z-index: 9997;
  }

  .navmenu ul {
    display: none;
    list-style: none;
    position: absolute;
    inset: 60px 20px 20px 20px;
    padding: 10px 0;
    margin: 0;
    border-radius: 6px;
    background-color: var(--nav-mobile-background-color);
    overflow-y: auto;
    transition: 0.3s;
    z-index: 9998;
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.1);
  }

  .navmenu a,
  .navmenu span,
  .navmenu a:focus {
    color: var(--nav-dropdown-color);
    padding: 10px 20px;
    font-family: var(--nav-font), serif;
    font-size: 17px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: space-between;
    white-space: nowrap;
    transition: 0.3s;
  }

  .navmenu a i,
  .navmenu span i,
  .navmenu span:focus i,
  .navmenu a:focus i {
    font-size: 12px;
    line-height: 0;
    margin-left: 5px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: 0.3s;
    background-color: color-mix(in srgb, var(--accent-color), transparent 90%);
  }

  .navmenu a i:hover,
  .navmenu span i:hover,
  .navmenu span:focus i:hover,
  .navmenu a:focus i:hover {
    background-color: var(--accent-color);
    color: var(--contrast-color);
    cursor: pointer;
  }

  .navmenu a:hover,
  .navmenu span:hover,
  .navmenu .active,
  .navmenu .active:focus {
    color: var(--nav-dropdown-hover-color);
    cursor: pointer;
  }

  .navmenu .active i,
  .navmenu .active:focus i {
    background-color: var(--accent-color);
    color: var(--contrast-color);
    transform: rotate(180deg);
  }

  .navmenu .dropdown ul {
    position: static;
    display: none;
    z-index: 99;
    padding: 10px 0;
    margin: 10px 20px;
    background-color: var(--nav-dropdown-background-color);
    border: 1px solid color-mix(in srgb, var(--default-color), transparent 90%);
    box-shadow: none;
    transition: all 0.5s ease-in-out;
  }

  .navmenu .dropdown ul ul {
    background-color: rgba(33, 37, 41, 0.1);
  }

  .navmenu .dropdown > .dropdown-active {
    display: block;
    background-color: rgba(33, 37, 41, 0.03);
  }

  .mobile-nav-active {
    overflow: hidden;
  }

  .mobile-nav-active .mobile-nav-toggle {
    color: #fff;
    position: absolute;
    right: 15px;
    margin-right: 0;
    z-index: 9999;
  }

  .mobile-nav-active .navmenu {
    position: fixed;
    overflow: hidden;
    inset: 0;
    background: rgba(33, 37, 41, 0.8);
    transition: 0.3s;
  }

  .mobile-nav-active .navmenu > ul {
    display: block;
  }
}
</style>